/**
 * Promotional Carousel Component (V3 Theme-Only)
 *
 * Renders promotional carousels from Budflow admin with V3 theme system.
 * Theme-only: always resolves a theme (DEFAULT_THEME fallback when no V3 theme set).
 * No hardcoded branding — all visual styling comes from the Budflow theme.
 *
 * Supports four display modes:
 * 1. Full Bleed Banner (banner_carousel with fullBleed=true)
 * 2. Container Banner (banner_carousel with fullBleed=false)
 * 3. Card Carousel (card_carousel with fixed pixels)
 * 4. Hero Carousel (card_carousel with widthPercent, or banner with heroMode)
 *
 * Usage:
 *   // In loader:
 *   const carousels = await fetchCarousels(['slot_xxx'], store);
 *
 *   // In component:
 *   <PromotionalCarousel data={carousels['slot_xxx']} />
 *
 * IMPORTANT — Focus rings (accessibility):
 * Focus-visible rings are NOT handled by this component. All carousel sections
 * render with `data-carousel-focus` so the consuming site can apply its own
 * focus ring styles consistently. When copying this component to a new site,
 * add the following rule to the site's global CSS:
 *
 *   [data-carousel-focus] :is(a, button):focus-visible {
 *     // your site's standard focus ring styles here
 *   }
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@remix-run/react';
import type {
  Carousel,
  CarouselSlide,
  CarouselCard,
  CarouselTheme,
  BannerLayoutConfig,
  CardLayoutConfig,
  HeroCardLayoutConfig,
  HeroSizingConfig,
  BannerPositions,
  ElementPosition,
} from '~/types/promotional-carousel';
import {
  parseNavMeta,
  parseSeeMoreMeta,
  parseCtaMeta,
  parseTitleAlign,
  resolveBreakpoint,
  resolvePositions,
  DEFAULT_POSITIONS,
  mergeThemeWithDefaults,
} from '~/lib/theme-runtime';
import { resolveImageUrl, isResponsiveUrl, buildSrcSet } from '~/utils/resolveImageUrl';

// =============================================================================
// TYPE GUARDS
// =============================================================================

function isHeroCardConfig(config: unknown): config is HeroCardLayoutConfig {
  if (!config || typeof config !== 'object') return false;
  const sizing = (config as Record<string, unknown>).sizing;
  if (!sizing || typeof sizing !== 'object') return false;
  const s = sizing as Record<string, unknown>;
  const mobile = s.mobile;
  const desktop = s.desktop;
  const hasMobileWidth =
    !!mobile &&
    typeof mobile === 'object' &&
    typeof (mobile as Record<string, unknown>).widthPercent === 'number';
  const hasDesktopWidth =
    !!desktop &&
    typeof desktop === 'object' &&
    typeof (desktop as Record<string, unknown>).widthPercent === 'number';
  return hasMobileWidth || hasDesktopWidth;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const TIMER_TICK_MS = 100;
const DEFAULT_BG_COLOR = '#ffffff';
const SCROLL_END_TIMEOUT = 150;

const LOOP_RESET_DELAY = 100;

const YOUTUBE_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})(?:[?&].*)?$/;

function extractYouTubeId(url: string): string | null {
  const match = url.trim().match(YOUTUBE_REGEX);
  return match ? match[1] : null;
}

/** Ref-based hook to track whether instant search is active. */
function useIsSearchOpen() {
  const ref = useRef(false);
  useEffect(() => {
    const handler = (e: Event) => {
      ref.current = (e as CustomEvent).detail.open;
    };
    window.addEventListener('search-toggle', handler);
    return () => window.removeEventListener('search-toggle', handler);
  }, []);
  return ref;
}

/** Signal product grid to scroll into view after a promo card navigates. */
function signalScrollToProducts() {
  try {
    sessionStorage.setItem('scrollToProductSection', Date.now().toString());
  } catch {
    /* SSR / incognito fallback */
  }
}

// =============================================================================
// THEME HELPERS
// =============================================================================

function getPos(
  positions: BannerPositions | undefined,
  device: 'desktop' | 'tablet' | 'mobile',
  element: keyof BannerPositions['desktop'],
): ElementPosition {
  if (device === 'tablet') {
    return (
      positions?.tablet?.[element] ||
      positions?.desktop?.[element] ||
      DEFAULT_POSITIONS.desktop[element]
    );
  }
  return positions?.[device]?.[element] || DEFAULT_POSITIONS[device][element];
}

/** Canonical widths matching the admin theme editor preview canvas.
 *  Stored position values are percentages of these canonical sizes. */
const CANONICAL_WIDTH = { desktop: 1280, tablet: 820, mobile: 390 } as const;

/** Resolve positioning style for an element.
 *  - "fixed" mode: converts percentages to fixed pixels using the admin's canonical
 *    canvas sizes (1280/820/390). Text stays put as viewport resizes within a breakpoint.
 *  - "responsive" mode (default): uses CSS percentages so content stays centered/positioned
 *    relative to the banner regardless of viewport width. */
function resolveElementStyle(
  el: ElementPosition,
  breakpoint: 'mobile' | 'tablet' | 'desktop',
  bannerHeight: number,
): {
  left: string;
  top: string;
  width?: string;
  maxWidth?: string;
  height?: string;
  maxHeight?: string;
} {
  const isFixed = el.positionMode === 'fixed';
  const cw = CANONICAL_WIDTH[breakpoint];
  return {
    left: isFixed ? `${Math.round((el.x / 100) * cw)}px` : `${el.x}%`,
    top: isFixed ? `${Math.round((el.y / 100) * bannerHeight)}px` : `${el.y}%`,
    ...(el.width && el.width > 0
      ? isFixed
        ? { width: `${Math.round((el.width / 100) * cw)}px`, maxWidth: '100%' }
        : { width: `${el.width}%` }
      : {}),
    ...(el.height && el.height > 0
      ? isFixed
        ? { height: `${Math.round((el.height / 100) * bannerHeight)}px`, maxHeight: '100%' }
        : { height: `${el.height}%` }
      : {}),
  };
}

/** HoverButton — button with JS-driven hover state for theme colors */
function HoverButton({
  style,
  hoverStyle,
  className,
  children,
  ...props
}: {
  style: React.CSSProperties;
  hoverStyle?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'>) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={className}
      style={{
        ...style,
        ...(hovered && hoverStyle ? hoverStyle : {}),
        transition: 'background-color 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s',
      }}
      {...props}
    >
      {children}
    </button>
  );
}

/** HoverSpan — span with JS-driven hover state (for CTAs inside linked slides) */
function HoverSpan({
  style,
  hoverStyle,
  className,
  children,
}: {
  style: React.CSSProperties;
  hoverStyle?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={className}
      style={{
        ...style,
        ...(hovered && hoverStyle ? hoverStyle : {}),
        transition: 'background-color 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s',
      }}
    >
      {children}
    </span>
  );
}

/** HoverLink — Link with JS-driven hover state for theme colors */
function HoverLink({
  to,
  style,
  hoverStyle,
  className,
  children,
  onClick,
  'aria-label': ariaLabel,
}: {
  to: string;
  style: React.CSSProperties;
  hoverStyle?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  'aria-label'?: string;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={className}
      aria-label={ariaLabel}
      style={{
        ...style,
        ...(hovered && hoverStyle ? hoverStyle : {}),
        transition: 'background-color 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s',
      }}
    >
      {children}
    </Link>
  );
}

/** Build hover style object for See More from parsed meta */
function buildSeeMoreHoverStyle(
  smMeta: ReturnType<typeof parseSeeMoreMeta>,
  baseStyle: React.CSSProperties,
): React.CSSProperties {
  if (smMeta.hoverOpacity) {
    return { opacity: parseFloat(smMeta.hoverOpacity) };
  }
  return {
    ...(smMeta.hoverBg ? { backgroundColor: smMeta.hoverBg } : {}),
    ...(smMeta.hoverText ? { color: smMeta.hoverText } : {}),
    ...(smMeta.hoverBorder && baseStyle.border
      ? {
          border: baseStyle.border
            .toString()
            .replace(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|transparent/, smMeta.hoverBorder),
        }
      : {}),
  };
}

// =============================================================================
// FONT LOADING
// =============================================================================

function ThemeFontLoader({
  fontCSS,
  googleFontsURL,
  preloadUrls,
}: {
  fontCSS?: string;
  googleFontsURL?: string | null;
  preloadUrls?: string[];
}) {
  return (
    <>
      {/* Preload custom font files — browser starts downloading immediately, before CSS parse */}
      {preloadUrls?.map((url) => {
        const ext = url.split('.').pop()?.toLowerCase();
        const mime =
          ext === 'woff2'
            ? 'font/woff2'
            : ext === 'woff'
              ? 'font/woff'
              : ext === 'otf'
                ? 'font/otf'
                : 'font/ttf';
        return (
          <link key={url} rel="preload" href={url} as="font" type={mime} crossOrigin="anonymous" />
        );
      })}
      {/* Preload Google Fonts stylesheet so it loads in parallel with HTML parsing */}
      {googleFontsURL && <link rel="preload" href={googleFontsURL} as="style" />}
      {fontCSS && <style dangerouslySetInnerHTML={{ __html: fontCSS }} />}
      {googleFontsURL && <link rel="stylesheet" href={googleFontsURL} />}
    </>
  );
}

// =============================================================================
// THEMED TITLE ROW
// =============================================================================

function ThemedTitleRow({
  theme,
  meta,
  carousel,
  navLeft,
  navRight,
  slideCount,
}: {
  theme: CarouselTheme;
  meta?: { title?: string; subtitle?: string };
  carousel: Carousel;
  navLeft: React.ReactNode;
  navRight: React.ReactNode;
  slideCount?: number;
}) {
  const isBanner = carousel.type === 'banner_carousel';
  const isSingleSlide = (slideCount ?? 2) <= 1;
  // Banners don't use a title row — content lives inside the banner per-slide
  const showTitle = !isBanner && theme.title.letterSpacing !== '__hidden__';
  const showSubtitle = !isBanner && theme.subtitle.weight !== '__hidden__';
  const desktopAlign = parseTitleAlign(theme.title.align, 'desktop');
  const tabletAlign = parseTitleAlign(theme.title.align, 'tablet');
  const mobileAlign = parseTitleAlign(theme.title.align, 'mobile');
  // Banners use dots/indicators only — no nav arrows in title row
  const showNavDesktop = !isBanner && theme.navigation.disabledOpacity !== 0;
  const navInTitleRow = theme.navigation.position === 'title-row';

  const smMeta = parseSeeMoreMeta(theme.seeMore.letterSpacing);
  const hideOnMobile = smMeta.mobilePosition === 'below';
  const hideOnTablet = smMeta.tabletPosition === 'below';
  const hideOnDesktop = smMeta.desktopPosition === 'below';
  // Banners have per-slide CTAs — no See More link
  const showSeeMore = !isBanner && theme.seeMore.show;

  if (!showTitle && !showSubtitle && !showSeeMore && !(showNavDesktop && navInTitleRow))
    return null;

  // Scope CSS classes per carousel to avoid collisions when multiple carousels render on the same page
  const cid = carousel.id.replace(/[^a-z0-9]/g, '');
  const cls = {
    align: `tr-align-${cid}`,
    heading: `tr-hdg-${cid}`,
    subtitle: `tr-sub-${cid}`,
    seeMore: `tr-sm-${cid}`,
  };

  const seeMoreEl = showSeeMore
    ? (() => {
        const baseStyle: React.CSSProperties = {
          padding: `${smMeta.py}px ${smMeta.px}px`,
          borderRadius: smMeta.radius,
          ...(smMeta.hasBg ? { backgroundColor: smMeta.bgColor } : {}),
          ...(smMeta.smBorder ? { border: smMeta.smBorder } : {}),
          fontFamily: `${theme.fonts.button}, sans-serif`,
          fontWeight: theme.seeMore.weight,
          color: theme.seeMore.color,
          whiteSpace: 'nowrap' as const,
        };
        // Build responsive visibility class for title-row CTA
        const visClass =
          hideOnMobile || hideOnTablet || hideOnDesktop ? cls.seeMore : 'inline-flex';
        return (
          <>
            <style>{`
            .${cls.seeMore}{display:${hideOnMobile ? 'none' : 'inline-flex'};font-size:${smMeta.mobileSize || theme.seeMore.size}}
            @media(min-width:768px){.${cls.seeMore}{display:${hideOnTablet ? 'none' : 'inline-flex'};font-size:${smMeta.tabletSize || theme.seeMore.size}}}
            @media(min-width:1024px){.${cls.seeMore}{display:${hideOnDesktop ? 'none' : 'inline-flex'};font-size:${theme.seeMore.size}}}
          `}</style>
            <HoverLink
              to={theme.seeMore.link || '/collection'}
              className={cls.seeMore}
              style={baseStyle}
              hoverStyle={buildSeeMoreHoverStyle(smMeta, baseStyle)}
            >
              <span aria-hidden="true">{theme.seeMore.text}</span>
              <span className="sr-only">See more products</span>
            </HoverLink>
          </>
        );
      })()
    : null;

  return (
    <div
      className="mx-auto max-w-[108rem] px-3 md:px-4 lg:px-5"
      style={{
        textAlign:
          mobileAlign !== desktopAlign || tabletAlign !== desktopAlign
            ? undefined
            : (desktopAlign as any),
      }}
    >
      {/* Responsive text-align when mobile/tablet differs from desktop */}
      {(mobileAlign !== desktopAlign || tabletAlign !== desktopAlign) && (
        <style>{`.${cls.align}{text-align:${mobileAlign}}@media(min-width:768px){.${cls.align}{text-align:${tabletAlign}}}@media(min-width:1024px){.${cls.align}{text-align:${desktopAlign}}}`}</style>
      )}
      <div
        className={`flex items-center justify-between ${mobileAlign !== desktopAlign || tabletAlign !== desktopAlign ? cls.align : ''}`}
      >
        <div className="min-w-0 flex-1">
          {showTitle && meta?.title && (
            <h2
              className={cls.heading}
              style={{
                fontFamily: `${theme.fonts.heading}, sans-serif`,
                fontWeight: theme.title.weight,
                textTransform: theme.title.transform || 'none',
                color: theme.colors.titleText,
              }}
            >
              <style>{`.${cls.heading}{font-size:${theme.title.size.mobile}}@media(min-width:640px){.${cls.heading}{font-size:${theme.title.size.desktop}}}`}</style>
              {meta.title}
            </h2>
          )}
          {showSubtitle && meta?.subtitle && (
            <p
              className={cls.subtitle}
              style={{
                fontFamily: `${theme.fonts.body}, sans-serif`,
                fontWeight: theme.subtitle.weight,
                color: theme.colors.bodyText,
                marginTop: '0.25rem',
              }}
            >
              <style>{`.${cls.subtitle}{font-size:${theme.subtitle.size.mobile}}@media(min-width:640px){.${cls.subtitle}{font-size:${theme.subtitle.size.desktop}}}`}</style>
              {meta.subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-5">
          {seeMoreEl}
          {showNavDesktop && navInTitleRow && !isSingleSlide && (
            <div className="hidden items-center gap-2 lg:flex">
              {navLeft}
              {navRight}
            </div>
          )}
          {theme.navigation.showOnMobile && navInTitleRow && !isSingleSlide && (
            <div className="flex items-center gap-2 sm:hidden">
              {navLeft}
              {navRight}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// THEMED NAV BUTTONS
// =============================================================================

function useThemedNav(theme: CarouselTheme) {
  const navMeta = parseNavMeta(theme.navigation.hoverBackgroundColor);

  const btnBase: React.CSSProperties = {
    width: theme.navigation.size,
    height: theme.navigation.size,
    borderRadius:
      theme.navigation.shape === 'circle' || theme.navigation.shape === 'pill' ? '9999px' : '0',
    backgroundColor:
      theme.navigation.shape !== 'bare' ? theme.navigation.backgroundColor : 'transparent',
    color: theme.navigation.iconColor,
    ...(navMeta.border ? { border: navMeta.border } : {}),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: 'pointer',
  };

  const hoverBorderCSS = navMeta.hoverBorder
    ? navMeta.border
      ? navMeta.border.replace(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|transparent/, navMeta.hoverBorder)
      : `1px solid ${navMeta.hoverBorder}`
    : undefined;

  const hoverStyle: React.CSSProperties = navMeta.hoverOpacity
    ? { opacity: parseFloat(navMeta.hoverOpacity) }
    : {
        ...(navMeta.hoverBg ? { backgroundColor: navMeta.hoverBg } : {}),
        ...(hoverBorderCSS ? { border: hoverBorderCSS } : {}),
        ...(navMeta.hoverIcon ? { color: navMeta.hoverIcon } : {}),
      };

  const iconSize = navMeta.iconSize;
  const isArrow = theme.navigation.style === 'arrow';

  return {
    btnBase,
    hoverStyle,
    iconSize,
    isArrow,
    disabledOpacity: theme.navigation.disabledOpacity,
  };
}

function ThemedNavButton({
  style,
  hoverStyle,
  disabled,
  direction,
  iconSize,
  isArrow,
  ariaLabel,
  onClick,
}: {
  style: React.CSSProperties;
  hoverStyle?: React.CSSProperties;
  disabled?: boolean;
  direction: 'left' | 'right';
  iconSize: string;
  isArrow: boolean;
  ariaLabel: string;
  onClick: () => void;
}) {
  const pathL = isArrow ? (
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5m7-7l-7 7 7 7" />
  ) : (
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  );
  const pathR = isArrow ? (
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-7-7l7 7-7 7" />
  ) : (
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  );

  return (
    <HoverButton
      style={{ ...style, ...(disabled ? { opacity: style.opacity, cursor: 'default' } : {}) }}
      hoverStyle={disabled ? undefined : hoverStyle}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <svg
        style={{ width: iconSize, height: iconSize }}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        {direction === 'left' ? pathL : pathR}
      </svg>
    </HoverButton>
  );
}

// =============================================================================
// THEMED DOTS / COUNTER
// =============================================================================

function ThemedIndicators({
  theme,
  count,
  current,
  onSelect,
}: {
  theme: CarouselTheme;
  count: number;
  current: number;
  onSelect: (i: number) => void;
}) {
  if (!theme.dots.show && !theme.counter.show) return null;
  return (
    <div className="flex items-center justify-center gap-1.5 py-2">
      {theme.dots.show && (
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {Array.from({ length: Math.min(count, theme.dots.maxDots || 7) }).map((_, i) => (
            <span
              key={i}
              style={{
                width:
                  theme.dots.style === 'bar'
                    ? '1.5rem'
                    : theme.dots.style === 'pill'
                      ? i === current
                        ? '1.5rem'
                        : theme.dots.size
                      : theme.dots.size,
                height: theme.dots.style === 'bar' ? '3px' : theme.dots.size,
                borderRadius: '9999px',
                backgroundColor:
                  theme.dots.style === 'outlined'
                    ? i === current
                      ? theme.dots.activeColor
                      : 'transparent'
                    : i === current
                      ? theme.dots.activeColor
                      : theme.dots.inactiveColor,
                border:
                  theme.dots.style === 'outlined'
                    ? `1.5px solid ${i === current ? theme.dots.activeColor : theme.dots.inactiveColor}`
                    : 'none',
                display: 'inline-block',
                transition: 'width 0.2s ease',
              }}
            />
          ))}
        </div>
      )}
      {theme.counter.show && (
        <span style={{ fontSize: theme.counter.size, color: theme.counter.color }} className="ml-2">
          {theme.counter.format === 'fraction'
            ? `${current + 1} / ${count}`
            : `${current + 1} of ${count}`}
        </span>
      )}
    </div>
  );
}

// =============================================================================
// PROPS
// =============================================================================

interface PromotionalCarouselProps {
  data: Carousel | null | undefined;
  className?: string;
  /** When true, wraps carousel in max-w-[108rem] container with padding */
  contained?: boolean;
}

// =============================================================================
// MAIN ENTRY
// =============================================================================

export default function PromotionalCarousel({
  data,
  className = '',
  contained = false,
}: PromotionalCarouselProps) {
  // Focus target for the "skip / exit carousel" affordance (see wrapper in the return).
  const carouselEndRef = useRef<HTMLDivElement>(null);

  if (!data) return null;

  // Theme-only: always populated (DEFAULT_THEME fallback from server)
  const theme = data.resolvedTheme!;

  let content: React.ReactElement | null = null;

  if (data.type === 'banner_carousel') {
    const config = data.layout_config as BannerLayoutConfig;
    if (config?.banner?.heroMode === true) {
      content = <HeroCarousel carousel={data} theme={theme} className={className} />;
    } else {
      content = <BannerCarousel carousel={data} theme={theme} className={className} />;
    }
  } else if (data.type === 'card_carousel') {
    if (isHeroCardConfig(data.layout_config)) {
      content = <HeroCarousel carousel={data} theme={theme} className={className} />;
    } else {
      content = <CardCarousel carousel={data} theme={theme} className={className} />;
    }
  }

  if (!content) return null;

  // A11Y — Let keyboard / screen-reader users bypass the carousel instead of tabbing
  // through every promotional card:
  //   • A visually-hidden "Skip promotions" link is the first focusable element; activating
  //     it jumps focus past the carousel.
  //   • Pressing Escape while anywhere inside the carousel does the same thing.
  // Both move focus to `carouselEndRef`, an off-screen marker placed right after the
  // carousel, so the next Tab continues on to the rest of the page.
  // PORTABLE: lives in the shared wrapper, so every variant and every site using this
  // component gets the same skip/exit behaviour for free.
  const body = (
    <div
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape') carouselEndRef.current?.focus();
      }}
    >
      <a
        href="#promotions-end"
        onClick={(e) => {
          e.preventDefault();
          carouselEndRef.current?.focus();
        }}
        // The skip link is the first focusable element of the carousel, so its description
        // is announced as the user enters — this is where we tell them Escape also exits,
        // since a keyboard shortcut isn't otherwise discoverable to a screen-reader user.
        aria-describedby={`carousel-esc-hint-${data.id}`}
        className="sr-only rounded focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-black focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2"
      >
        Skip promotions
      </a>
      <span id={`carousel-esc-hint-${data.id}`} className="sr-only">
        You can also press Escape to leave the carousel while browsing it.
      </span>
      {content}
      <div ref={carouselEndRef} tabIndex={-1} className="sr-only">
        End of promotions
      </div>
    </div>
  );

  return (
    <>
      <ThemeFontLoader
        fontCSS={data.fontCSS}
        googleFontsURL={data.googleFontsURL}
        preloadUrls={data.fontPreloadUrls}
      />
      {contained ? <div className="storefront-promotions mx-auto max-w-[105rem] lg:px-8">{body}</div> : body}
    </>
  );
}

// =============================================================================
// BANNER CAROUSEL
// =============================================================================

function BannerCarousel({
  carousel,
  theme,
  className = '',
}: {
  carousel: Carousel;
  theme: CarouselTheme;
  className?: string;
}) {
  const isSearchOpenRef = useIsSearchOpen();
  const [activeIndex, setActiveIndex] = useState(0);
  const [bgLayer, setBgLayer] = useState<{ color1: string; color2: string; showFirst: boolean }>({
    color1: DEFAULT_BG_COLOR,
    color2: DEFAULT_BG_COLOR,
    showFirst: true,
  });
  const [isPlaying, setIsPlaying] = useState(carousel.auto_advance);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeRemainingRef = useRef((carousel.auto_advance_interval || 5) * 1000);
  const isScrollingProgrammatically = useRef(false);
  const isUserScrollingRef = useRef(false);

  const slides = useMemo(() => carousel.content.slides || [], [carousel.content.slides]);
  const config = carousel.layout_config as BannerLayoutConfig;
  const intervalMs = (carousel.auto_advance_interval || 5) * 1000;

  // Toggle background layers for crossfade effect
  useEffect(() => {
    const newColor = slides[activeIndex]?.backgroundColor || DEFAULT_BG_COLOR;
    setBgLayer((prev) => ({
      color1: prev.showFirst ? prev.color1 : newColor,
      color2: prev.showFirst ? newColor : prev.color2,
      showFirst: !prev.showFirst,
    }));
  }, [activeIndex, slides]);

  const carouselId = `carousel-${carousel.id}`;

  // Auto-advance (respects prefers-reduced-motion)
  useEffect(() => {
    if (slides.length <= 1 || !isPlaying || isFocused) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsPlaying(false);
      return;
    }

    let lastTick = Date.now();

    intervalRef.current = setInterval(() => {
      if (isSearchOpenRef.current) return;
      if (isUserScrollingRef.current) return;

      const now = Date.now();
      const elapsed = now - lastTick;
      lastTick = now;

      timeRemainingRef.current -= elapsed;
      if (timeRemainingRef.current <= 0) {
        setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
        timeRemainingRef.current = intervalMs;
      }
    }, TIMER_TICK_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [slides.length, isPlaying, isFocused, intervalMs]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const resetAutoSlide = () => {
    timeRemainingRef.current = intervalMs;
  };

  const goToSlide = (index: number) => {
    if (index >= 0 && index < slides.length) {
      setActiveIndex(index);
      resetAutoSlide();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        goToSlide((activeIndex - 1 + slides.length) % slides.length);
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToSlide((activeIndex + 1) % slides.length);
        break;
      case ' ':
        e.preventDefault();
        setIsPlaying(!isPlaying);
        break;
    }
  };

  // Scroll to active slide
  useEffect(() => {
    const container = containerRef.current;
    if (!container || slides.length <= 1) return;

    const slideWidth = container.offsetWidth;
    const targetScroll = activeIndex * slideWidth;

    if (Math.abs(container.scrollLeft - targetScroll) > 5) {
      isScrollingProgrammatically.current = true;
      container.scrollTo({ left: targetScroll, behavior: 'smooth' });
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    }
  }, [activeIndex, slides.length]);

  // Sync activeIndex on user swipe
  useEffect(() => {
    const container = containerRef.current;
    if (!container || slides.length <= 1) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) return;
      isUserScrollingRef.current = true;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const slideWidth = container.offsetWidth;
        const newIndex = Math.floor((container.scrollLeft + slideWidth / 2) / slideWidth);
        if (newIndex !== activeIndex && newIndex >= 0 && newIndex < slides.length) {
          setActiveIndex(newIndex);
          resetAutoSlide();
        }
        isUserScrollingRef.current = false;
      }, 100);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      isUserScrollingRef.current = false;
    };
  }, [slides.length, activeIndex]);

  if (slides.length === 0) return null;

  const mobileHeight = config?.sizing?.mobile?.heightPx;
  const tabletHeight = config?.sizing?.tablet?.heightPx ?? config?.sizing?.desktop?.heightPx;
  const desktopHeight = config?.sizing?.desktop?.heightPx;
  const mobileWidthPercent = config?.sizing?.mobile?.widthPercent || 100;
  const desktopWidthPercent = config?.sizing?.desktop?.widthPercent || 100;
  const maxWidthPx = config?.sizing?.maxWidthPx;

  const isFullBleed =
    (config?.banner?.fullBleed ?? false) &&
    mobileWidthPercent === 100 &&
    desktopWidthPercent === 100 &&
    !maxWidthPx;

  const cssId = `banner-${carousel.id.replace(/[^a-z0-9]/g, '')}`;
  const positions = theme.bannerContent.positions || DEFAULT_POSITIONS;

  const nav = useThemedNav(theme);
  // Banners use dots/indicators only — no nav arrows
  const showNavDesktop = false;

  const makeNavBtn = (dir: 'left' | 'right', disabled?: boolean) => (
    <ThemedNavButton
      style={{ ...nav.btnBase, ...(disabled ? { opacity: nav.disabledOpacity } : {}) }}
      hoverStyle={nav.hoverStyle}
      disabled={disabled}
      direction={dir}
      iconSize={nav.iconSize}
      isArrow={nav.isArrow}
      ariaLabel={dir === 'left' ? 'Previous' : 'Next'}
      onClick={() =>
        goToSlide(
          dir === 'left'
            ? (activeIndex - 1 + slides.length) % slides.length
            : (activeIndex + 1) % slides.length,
        )
      }
    />
  );

  const tabletTopTitle =
    theme.bannerContent.topTitle.size.tablet ?? theme.bannerContent.topTitle.size.desktop;
  const tabletHeading =
    theme.bannerContent.heading.size.tablet ?? theme.bannerContent.heading.size.desktop;
  const tabletDesc =
    theme.bannerContent.description.size.tablet ?? theme.bannerContent.description.size.desktop;

  // Build scoped CSS with null-safe fallbacks to prevent hydration mismatch
  const ttMobile = theme.bannerContent.topTitle.size.mobile || '0.75rem';
  const ttTablet = tabletTopTitle || '0.875rem';
  const ttDesktop = theme.bannerContent.topTitle.size.desktop || '0.875rem';
  const hdMobile = theme.bannerContent.heading.size.mobile || '1.5rem';
  const hdTablet = tabletHeading || '3rem';
  const hdDesktop = theme.bannerContent.heading.size.desktop || '3rem';
  const dsMobile = theme.bannerContent.description.size.mobile || '0.875rem';
  const dsTablet = tabletDesc || '1.125rem';
  const dsDesktop = theme.bannerContent.description.size.desktop || '1.125rem';

  const scopedCSS = `.${cssId}-banner{height:${mobileHeight ? `${mobileHeight}px` : 'auto'}}@media(min-width:640px){.${cssId}-banner{height:${tabletHeight ? `${tabletHeight}px` : 'auto'}}}@media(min-width:1024px){.${cssId}-banner{height:${desktopHeight ? `${desktopHeight}px` : 'auto'}}}.${cssId} .theme-topTitle{font-size:${ttMobile}}.${cssId} .theme-heading{font-size:${hdMobile}}.${cssId} .theme-desc{font-size:${dsMobile}}.${cssId} .theme-cta{font-size:0.75rem}.${cssId} .txt-mobile{display:block}.${cssId} .txt-tablet{display:none}.${cssId} .txt-desktop{display:none}@media(min-width:640px){.${cssId} .theme-topTitle{font-size:${ttTablet}}.${cssId} .theme-heading{font-size:${hdTablet}}.${cssId} .theme-desc{font-size:${dsTablet}}.${cssId} .theme-cta{font-size:0.8125rem}.${cssId} .txt-mobile{display:none}.${cssId} .txt-tablet{display:block}}@media(min-width:1024px){.${cssId} .theme-topTitle{font-size:${ttDesktop}}.${cssId} .theme-heading{font-size:${hdDesktop}}.${cssId} .theme-desc{font-size:${dsDesktop}}.${cssId} .theme-cta{font-size:0.875rem}.${cssId} .txt-tablet{display:none}.${cssId} .txt-desktop{display:block}}`;

  const renderSlideContent = (slide: CarouselSlide) => {
    if (!slide.title && !slide.description && !slide.ctaText && !slide.topTitle) return null;

    // Per-slide theme override: merge slideTheme into carousel theme
    // Per-slide theme override: merge slideTheme into carousel theme (navigation stays global)
    const effectiveTheme = slide.slideTheme
      ? {
          ...mergeThemeWithDefaults({ ...theme, ...slide.slideTheme } as any),
          navigation: theme.navigation,
        }
      : theme;
    // Per-slide positions override
    const effectivePositions =
      slide.positions || effectiveTheme.bannerContent.positions || positions;
    const sMobilePos = effectivePositions.mobile || DEFAULT_POSITIONS.mobile;
    const sTabletPos =
      effectivePositions.tablet || effectivePositions.desktop || DEFAULT_POSITIONS.desktop;
    const sDesktopPos = effectivePositions.desktop || DEFAULT_POSITIONS.desktop;

    // When slide.ctaLink is set, the entire slide is wrapped in a <Link>/<a>,
    // so the CTA must render as a <span> to avoid nested <a> tags.
    const slideIsLinked = !!slide.ctaLink;

    const heightForBreakpoint = (bp: 'mobile' | 'tablet' | 'desktop') =>
      bp === 'mobile'
        ? mobileHeight || 440
        : bp === 'tablet'
          ? tabletHeight || 573
          : desktopHeight || 573;

    const ctaRenderer = (
      pos: BannerPositions['desktop'],
      breakpoint: 'mobile' | 'tablet' | 'desktop',
    ) => {
      if (!slide.ctaText) return null;
      const ctaMeta = parseCtaMeta(effectiveTheme.cta.letterSpacing);
      // Resolve CTA padding per breakpoint
      const ctaPadding =
        breakpoint === 'mobile' && effectiveTheme.cta.mobilePadding
          ? effectiveTheme.cta.mobilePadding
          : breakpoint === 'tablet' && effectiveTheme.cta.tabletPadding
            ? effectiveTheme.cta.tabletPadding
            : effectiveTheme.cta.padding;
      // Full-width CTA for mobile/tablet
      const isFullWidth =
        (breakpoint === 'mobile' && effectiveTheme.cta.fullWidthMobile) ||
        (breakpoint === 'tablet' && effectiveTheme.cta.fullWidthTablet);
      const ctaPos = resolveElementStyle(pos.cta, breakpoint, heightForBreakpoint(breakpoint));
      const ctaBase: React.CSSProperties = {
        left: isFullWidth ? '50%' : ctaPos.left,
        top: ctaPos.top,
        transform: 'translate(-50%, -50%)',
        backgroundColor:
          effectiveTheme.cta.style === 'filled' ? effectiveTheme.colors.accent : 'transparent',
        color:
          effectiveTheme.cta.style === 'filled'
            ? effectiveTheme.colors.accentText
            : effectiveTheme.colors.accent,
        border:
          effectiveTheme.cta.style === 'outline'
            ? `2px solid ${effectiveTheme.colors.accent}`
            : '2px solid transparent',
        borderRadius: effectiveTheme.cta.borderRadius,
        fontWeight: effectiveTheme.cta.weight,
        fontFamily: `${effectiveTheme.fonts.button}, sans-serif`,
        textTransform: effectiveTheme.cta.transform || 'none',
        padding: `${ctaPadding.y} ${ctaPadding.x}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        pointerEvents: 'auto',
        ...(isFullWidth ? { width: '95%', maxWidth: 'none' } : {}),
      };
      const ctaHover: React.CSSProperties = ctaMeta.hoverOpacity
        ? { opacity: parseFloat(ctaMeta.hoverOpacity) }
        : {
            ...(ctaMeta.hoverBg ? { backgroundColor: ctaMeta.hoverBg } : {}),
            ...(ctaMeta.hoverText ? { color: ctaMeta.hoverText } : {}),
            ...(ctaMeta.hoverBorder
              ? ctaBase.border
                ? {
                    border: ctaBase.border
                      .toString()
                      .replace(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|transparent/, ctaMeta.hoverBorder),
                  }
                : { border: `1px solid ${ctaMeta.hoverBorder}` }
              : {}),
          };
      // When the slide wrapper is already a <Link>, render CTA as a HoverSpan
      // to avoid invalid nested <a> tags. The click goes to the parent Link.
      if (slideIsLinked) {
        return (
          <HoverSpan className="theme-cta absolute" style={ctaBase} hoverStyle={ctaHover}>
            {slide.ctaText}
          </HoverSpan>
        );
      }
      if (!slide.ctaLink) return null;
      return (
        <HoverLink
          to={slide.ctaLink}
          className="theme-cta absolute"
          style={ctaBase}
          hoverStyle={ctaHover}
          onClick={signalScrollToProducts}
        >
          {slide.ctaText}
        </HoverLink>
      );
    };

    const renderTextLayer = (
      pos: BannerPositions['desktop'],
      layerClass: string,
      breakpoint: 'mobile' | 'tablet' | 'desktop',
    ) => {
      const bh = heightForBreakpoint(breakpoint);
      const ttStyle = resolveElementStyle(pos.topTitle, breakpoint, bh);
      const hdStyle = resolveElementStyle(pos.heading, breakpoint, bh);
      const dsStyle = resolveElementStyle(pos.description, breakpoint, bh);

      // Inline fontSize from effectiveTheme overrides scoped CSS, ensuring per-slide overrides work
      const ttFontSize = resolveBreakpoint(effectiveTheme.bannerContent.topTitle.size, breakpoint);
      const hdFontSize = resolveBreakpoint(effectiveTheme.bannerContent.heading.size, breakpoint);
      const dsFontSize = resolveBreakpoint(
        effectiveTheme.bannerContent.description.size,
        breakpoint,
      );

      return (
        <div className={`absolute inset-0 ${layerClass}`} style={{ pointerEvents: 'none' }}>
          {slide.topTitle && (
            <span
              className="theme-topTitle absolute"
              style={{
                left: ttStyle.left,
                top: ttStyle.top,
                transform: 'translate(-50%, -50%)',
                ...(ttStyle.width ? { width: ttStyle.width, maxWidth: ttStyle.maxWidth } : {}),
                ...(ttStyle.height ? { height: ttStyle.height, maxHeight: ttStyle.maxHeight } : {}),
                fontSize: ttFontSize,
                fontWeight: effectiveTheme.bannerContent.topTitle.weight,
                color: effectiveTheme.bannerContent.topTitle.color,
                textTransform: effectiveTheme.bannerContent.topTitle.transform,
                letterSpacing: effectiveTheme.bannerContent.topTitle.letterSpacing,
                lineHeight: effectiveTheme.bannerContent.topTitle.lineHeight || 'normal',
                textAlign: effectiveTheme.bannerContent.topTitle.align || 'center',
                fontFamily: effectiveTheme.bannerContent.topTitle.font
                  ? `${effectiveTheme.bannerContent.topTitle.font}, sans-serif`
                  : undefined,
                whiteSpace: pos.topTitle.width ? 'normal' : 'nowrap',
                overflow: 'hidden',
                pointerEvents: 'auto',
              }}
            >
              {slide.topTitle}
            </span>
          )}
          {slide.title && (
            <h2
              className="theme-heading absolute"
              style={{
                left: hdStyle.left,
                top: hdStyle.top,
                transform: 'translate(-50%, -50%)',
                ...(hdStyle.width ? { width: hdStyle.width, maxWidth: hdStyle.maxWidth } : {}),
                ...(hdStyle.height ? { height: hdStyle.height, maxHeight: hdStyle.maxHeight } : {}),
                fontSize: hdFontSize,
                fontFamily: `${effectiveTheme.fonts.heading}, sans-serif`,
                fontWeight: effectiveTheme.bannerContent.heading.weight,
                color: effectiveTheme.bannerContent.heading.color,
                textTransform: effectiveTheme.bannerContent.heading.transform,
                lineHeight: effectiveTheme.bannerContent.heading.lineHeight,
                maxWidth: pos.heading.width ? undefined : effectiveTheme.bannerContent.maxWidth,
                letterSpacing: effectiveTheme.bannerContent.heading.letterSpacing || 'normal',
                textAlign: effectiveTheme.bannerContent.heading.align || 'center',
                margin: 0,
                overflow: 'hidden',
                pointerEvents: 'auto',
              }}
            >
              {slide.title}
            </h2>
          )}
          {slide.description && (
            <p
              className="theme-desc absolute"
              style={{
                left: dsStyle.left,
                top: dsStyle.top,
                transform: 'translate(-50%, -50%)',
                ...(dsStyle.width ? { width: dsStyle.width, maxWidth: dsStyle.maxWidth } : {}),
                ...(dsStyle.height ? { height: dsStyle.height, maxHeight: dsStyle.maxHeight } : {}),
                fontSize: dsFontSize,
                fontFamily: `${effectiveTheme.fonts.body}, sans-serif`,
                fontWeight: effectiveTheme.bannerContent.description.weight,
                color: effectiveTheme.bannerContent.description.color,
                textTransform: effectiveTheme.bannerContent.description.transform || 'none',
                lineHeight: effectiveTheme.bannerContent.description.lineHeight || 'normal',
                letterSpacing: effectiveTheme.bannerContent.description.letterSpacing || 'normal',
                textAlign: effectiveTheme.bannerContent.description.align || 'center',
                margin: 0,
                maxWidth: pos.description.width ? undefined : '90%',
                overflow: 'hidden',
                pointerEvents: 'auto',
              }}
            >
              {slide.description}
            </p>
          )}
          {ctaRenderer(pos, breakpoint)}
        </div>
      );
    };

    return (
      <>
        {renderTextLayer(sMobilePos, 'txt-mobile', 'mobile')}
        {renderTextLayer(sTabletPos, 'txt-tablet', 'tablet')}
        {renderTextLayer(sDesktopPos, 'txt-desktop', 'desktop')}
      </>
    );
  };

  // Full bleed mode
  if (isFullBleed) {
    return (
      <section
        data-carousel-focus
        className={`relative w-full overflow-hidden ${cssId} ${className}`}
        style={{ fontFamily: `${theme.fonts.body}, sans-serif` }}
      >
        <style>{scopedCSS}</style>

        <ThemedTitleRow
          theme={theme}
          meta={carousel.content.meta}
          carousel={carousel}
          navLeft={makeNavBtn('left', activeIndex === 0)}
          navRight={makeNavBtn('right', activeIndex === slides.length - 1)}
          slideCount={slides.length}
        />

        <div
          ref={containerRef}
          onKeyDown={handleKeyDown}
          tabIndex={slides.length > 1 ? 0 : -1}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsFocused(false);
          }}
          className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {slides.map((slide, index) => (
            <BannerSlide
              key={slide.id || `slide-${index}`}
              slide={slide}
              index={index}
              isActive={index === activeIndex}
              isPlaying={isPlaying}
              carouselId={carousel.id}
              carouselName={carousel.name}
              totalSlides={slides.length}
              ariaCarouselId={carouselId}
              theme={theme}
              cssId={cssId}
              renderSlideContent={renderSlideContent}
              fullBleed
            />
          ))}
        </div>

        {/* Inside nav */}
        {showNavDesktop && theme.navigation.position === 'inside' && slides.length > 1 && (
          <>
            <div className="absolute top-1/2 left-4 z-10 hidden -translate-y-1/2 lg:block">
              {makeNavBtn('left', activeIndex === 0)}
            </div>
            <div className="absolute top-1/2 right-4 z-10 hidden -translate-y-1/2 lg:block">
              {makeNavBtn('right', activeIndex === slides.length - 1)}
            </div>
          </>
        )}

        {/* Below nav */}
        {showNavDesktop && theme.navigation.position === 'below' && slides.length > 1 && (
          <div className="hidden justify-center gap-4 py-2 lg:flex">
            {makeNavBtn('left', activeIndex === 0)}
            {makeNavBtn('right', activeIndex === slides.length - 1)}
          </div>
        )}

        {slides.length > 1 && (
          <ThemedIndicators
            theme={theme}
            count={slides.length}
            current={activeIndex}
            onSelect={goToSlide}
          />
        )}

        <p className="sr-only">Use left and right arrow keys to navigate between slides.</p>
      </section>
    );
  }

  // Container mode — banner carousels don't use sectionBackground (use per-slide backgroundColor instead)
  return (
    <section
      data-carousel-focus
      className={`relative w-full overflow-hidden ${cssId} ${className}`}
      style={{ fontFamily: `${theme.fonts.body}, sans-serif` }}
    >
      <style>
        {scopedCSS +
          `.${cssId}-slide-wrapper{width:${mobileWidthPercent}vw;${maxWidthPx ? `max-width:${maxWidthPx}px;` : ''}}@media(min-width:640px){.${cssId}-slide-wrapper{width:${config?.sizing?.tablet?.widthPercent ?? desktopWidthPercent}vw}}@media(min-width:1024px){.${cssId}-slide-wrapper{width:${desktopWidthPercent}vw}}`}
      </style>

      <ThemedTitleRow
        theme={theme}
        meta={carousel.content.meta}
        carousel={carousel}
        navLeft={makeNavBtn('left', activeIndex === 0)}
        navRight={makeNavBtn('right', activeIndex === slides.length - 1)}
        slideCount={slides.length}
      />

      <div className="relative py-4 md:py-6 lg:py-8">
        {/* Background crossfade layers */}
        <div
          className={`absolute inset-0 transition-opacity duration-500 ease-in-out motion-reduce:duration-1000 ${bgLayer.showFirst ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundColor: bgLayer.color1 }}
          aria-hidden="true"
        />
        <div
          className={`absolute inset-0 transition-opacity duration-500 ease-in-out motion-reduce:duration-1000 ${bgLayer.showFirst ? 'opacity-0' : 'opacity-100'}`}
          style={{ backgroundColor: bgLayer.color2 }}
          aria-hidden="true"
        />

        <div className="relative mx-auto flex justify-center">
          <div
            className={`${cssId}-slide-wrapper ${cssId}-banner relative overflow-hidden`}
            role="region"
            aria-roledescription="carousel"
            aria-label={carousel.content.meta?.title || carousel.name}
            style={{
              borderRadius: theme.bannerContent.borderRadius || '0',
              ...(theme.bannerContent.borderColor && theme.bannerContent.borderWidth
                ? {
                    border: `${theme.bannerContent.borderWidth} solid ${theme.bannerContent.borderColor}`,
                  }
                : {}),
            }}
          >
            <div
              ref={containerRef}
              onKeyDown={handleKeyDown}
              tabIndex={slides.length > 1 ? 0 : -1}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsFocused(false);
              }}
              className="flex h-full snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {slides.map((slide, index) => (
                <BannerSlide
                  key={slide.id || `slide-${index}`}
                  slide={slide}
                  index={index}
                  isActive={index === activeIndex}
                  isPlaying={isPlaying}
                  carouselId={carousel.id}
                  carouselName={carousel.name}
                  totalSlides={slides.length}
                  ariaCarouselId={carouselId}
                  theme={theme}
                  cssId={cssId}
                  renderSlideContent={renderSlideContent}
                />
              ))}
            </div>

            {/* Inside nav */}
            {showNavDesktop && theme.navigation.position === 'inside' && slides.length > 1 && (
              <>
                <div className="absolute top-1/2 left-4 z-10 hidden -translate-y-1/2 lg:block">
                  {makeNavBtn('left', activeIndex === 0)}
                </div>
                <div className="absolute top-1/2 right-4 z-10 hidden -translate-y-1/2 lg:block">
                  {makeNavBtn('right', activeIndex === slides.length - 1)}
                </div>
              </>
            )}

            <p className="sr-only">Use left and right arrow keys to navigate between slides.</p>
          </div>
        </div>
      </div>

      {/* Below nav */}
      {showNavDesktop && theme.navigation.position === 'below' && slides.length > 1 && (
        <div className="hidden justify-center gap-4 py-2 lg:flex">
          {makeNavBtn('left', activeIndex === 0)}
          {makeNavBtn('right', activeIndex === slides.length - 1)}
        </div>
      )}

      {slides.length > 1 && (
        <ThemedIndicators
          theme={theme}
          count={slides.length}
          current={activeIndex}
          onSelect={goToSlide}
        />
      )}

      {/* Below See More — not used for banners (each slide has its own CTA) */}
    </section>
  );
}

// =============================================================================
// BANNER SLIDE
// =============================================================================

interface BannerSlideProps {
  slide: CarouselSlide;
  index: number;
  isActive: boolean;
  isPlaying: boolean;
  carouselId: string;
  carouselName: string;
  totalSlides: number;
  ariaCarouselId: string;
  theme: CarouselTheme;
  cssId: string;
  renderSlideContent: (slide: CarouselSlide) => React.ReactNode;
  fullBleed?: boolean;
}

function BannerSlide({
  slide,
  index,
  isActive,
  isPlaying,
  carouselId,
  carouselName,
  totalSlides,
  ariaCarouselId,
  theme,
  cssId,
  renderSlideContent,
  fullBleed = false,
}: BannerSlideProps) {
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const shouldPlay = isActive && isPlaying;
    [desktopVideoRef.current, mobileVideoRef.current].forEach((video) => {
      if (!video) return;
      if (shouldPlay) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [isActive, isPlaying]);

  const isVideo = slide.mediaType === 'video';
  const mobileVideoSrc = slide.mobileVideo || slide.desktopVideo;
  const desktopVideoSrc = slide.desktopVideo || slide.mobileVideo;
  const sameImage =
    slide.desktopImage && slide.mobileImage && slide.desktopImage === slide.mobileImage;
  const rawImgSrc = slide.desktopImage || slide.mobileImage;
  const responsive = sameImage && isResponsiveUrl(rawImgSrc);
  const mobileImgSrc = responsive
    ? resolveImageUrl(rawImgSrc, 'sm')
    : resolveImageUrl(slide.mobileImage || slide.desktopImage, 'sm');
  const desktopImgSrc = responsive
    ? resolveImageUrl(rawImgSrc, 'lg')
    : resolveImageUrl(slide.desktopImage || slide.mobileImage, 'lg');
  const isFirstSlide = index === 0;

  const wrapperClasses = fullBleed
    ? 'relative w-full flex-shrink-0 snap-start'
    : 'relative h-full w-full flex-shrink-0 snap-start';

  const innerContent = (
    <div
      id={`${ariaCarouselId}-panel-${index}`}
      role="group"
      aria-roledescription="slide"
      // Position only — the role description already says "slide" (avoids "slide" twice).
      aria-label={`${index + 1} of ${totalSlides}`}
      aria-hidden={!isActive}
      className={fullBleed ? `${cssId}-banner relative` : 'relative h-full'}
    >
      <div
        className={
          fullBleed ? 'block cursor-pointer select-none' : 'block h-full cursor-pointer select-none'
        }
      >
        {isVideo && (mobileVideoSrc || desktopVideoSrc) ? (
          <>
            <video
              ref={desktopVideoRef}
              className={
                fullBleed
                  ? 'hidden w-full md:block'
                  : 'absolute inset-0 hidden h-full w-full object-cover md:block'
              }
              autoPlay
              loop
              muted
              playsInline
              preload={isFirstSlide ? 'auto' : 'metadata'}
              aria-hidden="true"
            >
              <source src={desktopVideoSrc || ''} type="video/mp4" />
            </video>
            <video
              ref={mobileVideoRef}
              className={
                fullBleed
                  ? 'block w-full md:hidden'
                  : 'absolute inset-0 block h-full w-full object-cover md:hidden'
              }
              autoPlay
              loop
              muted
              playsInline
              preload={isFirstSlide ? 'auto' : 'metadata'}
              aria-hidden="true"
            >
              <source src={mobileVideoSrc || ''} type="video/mp4" />
            </video>
          </>
        ) : (
          <picture>
            {!responsive && mobileImgSrc && mobileImgSrc !== desktopImgSrc && (
              <source media="(max-width: 767px)" srcSet={mobileImgSrc} />
            )}
            <img
              src={desktopImgSrc || mobileImgSrc || ''}
              {...(responsive && rawImgSrc
                ? {
                    srcSet: buildSrcSet(rawImgSrc),
                    sizes: '100vw',
                  }
                : {})}
              alt={slide.desktopAlt || slide.mobileAlt || ''}
              className={fullBleed ? 'block w-full' : 'absolute inset-0 h-full w-full object-cover'}
              loading={isFirstSlide ? 'eager' : 'lazy'}
            />
          </picture>
        )}

        {/* Overlay */}
        {theme.bannerContent.overlay.enabled && (
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: theme.bannerContent.overlay.color,
              background: theme.bannerContent.overlay.gradient,
            }}
          />
        )}

        {/* Text overlay */}
        {renderSlideContent(slide)}
      </div>
    </div>
  );

  if (slide.ctaLink) {
    return (
      <Link
        to={slide.ctaLink}
        className={wrapperClasses}
        tabIndex={isActive ? 0 : -1}
        aria-label={`${slide.title || 'Promotional slide'}${slide.description ? `: ${slide.description}` : ''}. Click to ${slide.ctaText || 'learn more'}`}
        onClick={signalScrollToProducts}
      >
        {innerContent}
      </Link>
    );
  }

  return <div className={wrapperClasses}>{innerContent}</div>;
}

// =============================================================================
// HERO CAROUSEL
// =============================================================================

function HeroCarousel({
  carousel,
  theme,
  className = '',
}: {
  carousel: Carousel;
  theme: CarouselTheme;
  className?: string;
}) {
  const isSearchOpenRef = useIsSearchOpen();
  const carouselRef = useRef<HTMLDivElement>(null);
  const isLoopingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeRemainingRef = useRef((carousel.auto_advance_interval || 5) * 1000);
  const isUserScrollingRef = useRef(false);
  const lastScrollPositionRef = useRef(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(carousel.auto_advance);
  const [isFocused, setIsFocused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const [visualDotIndex, setVisualDotIndex] = useState(0);

  const items = useMemo(() => {
    if (carousel.type === 'card_carousel') return carousel.content.cards || [];
    return carousel.content.slides || [];
  }, [carousel.content.cards, carousel.content.slides, carousel.type]);

  const totalItems = items.length;
  const meta = carousel.content.meta;
  const config = carousel.layout_config;
  const intervalMs = (carousel.auto_advance_interval || 5) * 1000;

  const sizing = config?.sizing as Partial<HeroSizingConfig> | undefined;
  const mobileHeight = sizing?.mobile?.heightPx ?? 155;
  const desktopHeight = sizing?.desktop?.heightPx ?? 290;
  const mobileWidthPercent = sizing?.mobile?.widthPercent ?? 85;
  const desktopWidthPercent = sizing?.desktop?.widthPercent ?? 95;
  const maxWidthPx = sizing?.mobile?.maxWidthPx ?? (sizing as any)?.maxWidthPx ?? 1160;
  const snapAlign =
    (config as unknown as Record<string, unknown>)?.snapAlign === 'center' ? 'center' : 'start';

  // Only triple items when auto-advance is on (infinite loop needed)
  // Non-looping carousels just render the original items — better performance
  const needsLoop = carousel.auto_advance && totalItems > 1;
  const displayItems = useMemo(() => {
    if (totalItems <= 1) return items;
    return needsLoop ? [...items, ...items, ...items] : items;
  }, [items, totalItems, needsLoop]);

  const getOriginalIndex = useCallback(
    (displayIndex: number): number => {
      if (totalItems <= 1) return 0;
      return displayIndex % totalItems;
    },
    [totalItems],
  );

  // Initialize: jump to middle set so loop resets don't fire during forward auto-advance
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    if (needsLoop && totalItems > 1 && carouselRef.current) {
      const middleStart = totalItems;
      const slides = carouselRef.current.querySelectorAll<HTMLElement>('[data-hero-slide]');
      const targetSlide = slides[middleStart];
      if (targetSlide) {
        const scrollPad =
          parseFloat(getComputedStyle(carouselRef.current).scrollPaddingLeft || '0') || 0;
        carouselRef.current.scrollTo({
          left: Math.max(0, targetSlide.offsetLeft - scrollPad),
          behavior: 'instant' as ScrollBehavior,
        });
        lastScrollPositionRef.current = middleStart;
        setCurrentIndex(middleStart);
      }
    }

    setIsInitialized(true);
  }, [totalItems]);

  // Auto-advance
  useEffect(() => {
    if (totalItems <= 1 || !isPlaying || !isInitialized || isFocused) return;
    if (typeof window !== 'undefined') {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        setIsPlaying(false);
        return;
      }
    }

    let lastTick = Date.now();
    const interval = setInterval(() => {
      const now = Date.now();
      if (isSearchOpenRef.current || isUserScrollingRef.current || isLoopingRef.current) {
        lastTick = now; // Keep lastTick fresh so elapsed doesn't accumulate while paused
        return;
      }
      const elapsed = now - lastTick;
      lastTick = now;
      timeRemainingRef.current -= elapsed;
      if (timeRemainingRef.current <= 0) {
        const next = lastScrollPositionRef.current + 1;
        isProgrammaticScrollRef.current = true;
        lastScrollPositionRef.current = next;
        setCurrentIndex(next);
        timeRemainingRef.current = intervalMs;
      }
    }, TIMER_TICK_MS);

    return () => clearInterval(interval);
  }, [totalItems, isPlaying, isFocused, intervalMs, isInitialized]);

  // Smooth scroll to current slide
  useEffect(() => {
    if (!carouselRef.current || totalItems <= 1 || !isInitialized) return;
    if (isLoopingRef.current) return;

    const carouselEl = carouselRef.current;
    const slides = carouselEl.querySelectorAll<HTMLElement>('[data-hero-slide]');
    const numItems = slides.length;
    if (numItems === 0) return;

    const displayIndex = currentIndex % numItems;
    if (displayIndex < 0 || displayIndex >= numItems) return;
    const targetSlide = slides[displayIndex];
    if (!targetSlide) return;

    if (programmaticTimeoutRef.current) clearTimeout(programmaticTimeoutRef.current);
    isProgrammaticScrollRef.current = true;

    // scrollTo on the carousel container only — avoids scrollIntoView moving the whole page
    const scrollPad = parseFloat(getComputedStyle(carouselEl).scrollPaddingLeft || '0') || 0;
    const targetLeft = Math.max(0, targetSlide.offsetLeft - scrollPad);
    carouselEl.style.scrollSnapType = 'none';
    carouselEl.scrollTo({ left: targetLeft, behavior: 'smooth' });

    // After smooth scroll completes, snap exactly to the target position before
    // re-enabling snap — prevents the browser from snapping to an adjacent card
    // due to subpixel rounding.
    programmaticTimeoutRef.current = setTimeout(() => {
      carouselEl.scrollTo({ left: targetLeft, behavior: 'instant' as ScrollBehavior });
      requestAnimationFrame(() => {
        carouselEl.style.scrollSnapType = '';
        isProgrammaticScrollRef.current = false;
      });
    }, 600);
  }, [currentIndex, totalItems, isInitialized, displayItems.length, snapAlign]);

  // Seamless looping
  useEffect(() => {
    if (!carouselRef.current || totalItems <= 2) return;

    const carouselEl = carouselRef.current;
    let scrollTimeout: NodeJS.Timeout;
    let rafId: number | null = null;

    // For snap-start, detect current card by left edge; for snap-center, by viewport center
    const scrollPadPx = parseFloat(getComputedStyle(carouselEl).scrollPaddingLeft || '0') || 0;
    const findClosestSlide = (slides: NodeListOf<HTMLElement>) => {
      const anchorX =
        snapAlign === 'center'
          ? carouselEl.scrollLeft + carouselEl.offsetWidth / 2
          : carouselEl.scrollLeft + scrollPadPx;
      let closestIdx = 0;
      let closestDist = Infinity;
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        if (!slide) continue;
        const slideAnchor =
          snapAlign === 'center' ? slide.offsetLeft + slide.offsetWidth / 2 : slide.offsetLeft;
        const dist = Math.abs(anchorX - slideAnchor);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      }
      return closestIdx;
    };

    const handleScrollEnd = () => {
      if (!isLoopingRef.current && !isProgrammaticScrollRef.current) {
        isUserScrollingRef.current = true;
      }

      if (!isLoopingRef.current && !isProgrammaticScrollRef.current) {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const slides = carouselEl.querySelectorAll<HTMLElement>('[data-hero-slide]');
          const closestIdx = findClosestSlide(slides);
          setVisualDotIndex(closestIdx % totalItems);
        });
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (isLoopingRef.current || isProgrammaticScrollRef.current || !carouselEl) {
          isUserScrollingRef.current = false;
          return;
        }

        const slides = carouselEl.querySelectorAll<HTMLElement>('[data-hero-slide]');
        if (slides.length === 0) {
          isUserScrollingRef.current = false;
          return;
        }

        const closestIndex = findClosestSlide(slides);

        const setSize = totalItems;
        if (hasInitializedRef.current && (closestIndex < setSize || closestIndex >= setSize * 2)) {
          isLoopingRef.current = true;
          const normalizedIndex = closestIndex % setSize;
          const middleSetIndex = normalizedIndex + setSize;
          const targetSlide = slides[middleSetIndex];

          if (targetSlide && targetSlide.offsetWidth > 0) {
            const jumpLeft =
              snapAlign === 'center'
                ? targetSlide.offsetLeft - (carouselEl.offsetWidth - targetSlide.offsetWidth) / 2
                : Math.max(0, targetSlide.offsetLeft - scrollPadPx);
            carouselEl.scrollTo({ left: jumpLeft, behavior: 'instant' as ScrollBehavior });
            lastScrollPositionRef.current = middleSetIndex;
            setCurrentIndex(middleSetIndex);
            setTimeout(() => {
              isLoopingRef.current = false;
              isUserScrollingRef.current = false;
              timeRemainingRef.current = intervalMs;
            }, LOOP_RESET_DELAY);
          } else {
            isUserScrollingRef.current = false;
          }
        } else if (hasInitializedRef.current) {
          lastScrollPositionRef.current = closestIndex;
          setVisualDotIndex(closestIndex % totalItems);
          isUserScrollingRef.current = false;
          timeRemainingRef.current = intervalMs;
        }
      }, SCROLL_END_TIMEOUT);
    };

    carouselEl.addEventListener('scroll', handleScrollEnd, { passive: true });
    return () => {
      carouselEl.removeEventListener('scroll', handleScrollEnd);
      clearTimeout(scrollTimeout);
      if (rafId) cancelAnimationFrame(rafId);
      isUserScrollingRef.current = false;
    };
  }, [totalItems, intervalMs]);

  useEffect(() => {
    setVisualDotIndex(getOriginalIndex(currentIndex));
  }, [currentIndex, getOriginalIndex]);

  const goToSlide = (originalIndex: number) => {
    if (totalItems <= 1) {
      setCurrentIndex(0);
      return;
    }
    if (!hasInitializedRef.current) hasInitializedRef.current = true;

    const candidates = [originalIndex + totalItems, originalIndex + totalItems * 2];
    if (originalIndex > 0) candidates.push(originalIndex);

    let nearest = candidates[0];
    let nearestDist = Infinity;
    for (const c of candidates) {
      const dist = Math.abs(c - lastScrollPositionRef.current);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = c;
      }
    }

    isProgrammaticScrollRef.current = true;
    lastScrollPositionRef.current = nearest;
    setCurrentIndex(nearest);
    timeRemainingRef.current = intervalMs;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (totalItems <= 1) return;
    switch (e.key) {
      case 'ArrowLeft': {
        e.preventDefault();
        const prevIdx = lastScrollPositionRef.current - 1;
        lastScrollPositionRef.current = prevIdx;
        setCurrentIndex(prevIdx);
        timeRemainingRef.current = intervalMs;
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        const nextIdx = lastScrollPositionRef.current + 1;
        lastScrollPositionRef.current = nextIdx;
        setCurrentIndex(nextIdx);
        timeRemainingRef.current = intervalMs;
        break;
      }
      case ' ':
        e.preventDefault();
        setIsPlaying(!isPlaying);
        break;
    }
  };

  const nav = useThemedNav(theme);

  if (items.length === 0) return null;

  const cssId = `hero-${carousel.id.replace(/[^a-z0-9]/g, '')}`;
  const cardRadius = theme.card.borderRadius;
  const showBelowText = theme.card.titleColor !== '__hidden__';
  const cardGap = typeof theme.card.gap === 'string' ? theme.card.gap : undefined;
  const mobileGap =
    typeof theme.card.gap === 'object' ? theme.card.gap.mobile : cardGap || '0.75rem';
  const desktopGap =
    typeof theme.card.gap === 'object' ? theme.card.gap.desktop : cardGap || '1rem';
  const makeNavBtn = (dir: 'left' | 'right') => (
    <ThemedNavButton
      style={nav.btnBase}
      hoverStyle={nav.hoverStyle}
      direction={dir}
      iconSize={nav.iconSize}
      isArrow={nav.isArrow}
      ariaLabel={dir === 'left' ? 'Previous' : 'Next'}
      onClick={() => {
        if (!isLoopingRef.current) {
          const next =
            dir === 'left' ? lastScrollPositionRef.current - 1 : lastScrollPositionRef.current + 1;
          lastScrollPositionRef.current = next;
          setCurrentIndex(next);
          timeRemainingRef.current = intervalMs;
        }
      }}
    />
  );

  return (
    <section
      data-carousel-focus
      className={`relative w-full py-2 ${className}`}
      style={{
        backgroundColor: theme.colors.sectionBackground ?? undefined,
        fontFamily: `${theme.fonts.body}, sans-serif`,
      }}
    >
      <style>{`
        .${cssId} { width: ${mobileWidthPercent}%; max-width: ${maxWidthPx}px; }
        .${cssId}-img { height: ${mobileHeight}px; position: relative; }
        .${cssId}-scroll { gap: ${mobileGap}; padding-left: ${mobileGap}; scroll-padding-left: ${mobileGap}; padding-right: ${mobileGap}; }
        @media (min-width: 500px) {
          .${cssId} { width: ${desktopWidthPercent}%; }
          .${cssId}-img { height: auto; aspect-ratio: ${maxWidthPx} / ${desktopHeight}; }
          .${cssId}-scroll { gap: ${desktopGap}; padding-left: ${desktopGap}; scroll-padding-left: ${desktopGap}; padding-right: 0; }
        }
      `}</style>

      <ThemedTitleRow
        theme={theme}
        meta={meta}
        carousel={carousel}
        navLeft={makeNavBtn('left')}
        navRight={makeNavBtn('right')}
        slideCount={items.length}
      />

      <div
        className="relative"
        onMouseEnter={() => setIsPlaying(false)}
        onMouseLeave={() => setIsPlaying(true)}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsFocused(false);
        }}
        onKeyDown={handleKeyDown}
        role="region"
        aria-roledescription="carousel"
        aria-label={meta?.title || carousel.name}
        tabIndex={totalItems > 1 ? 0 : -1}
      >
        <div
          ref={carouselRef}
          className={`${cssId}-scroll flex snap-x snap-mandatory items-start overflow-x-auto py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
        >
          {snapAlign === 'center' && (
            <div className="w-[3px] flex-shrink-0 snap-start" aria-hidden="true" />
          )}
          {displayItems.map((item, index) => {
            const itemVideoUrl = (item as CarouselCard).videoUrl || '';
            const hasItemVideo = !!(itemVideoUrl && extractYouTubeId(itemVideoUrl));
            const cardItem = item as CarouselCard;
            const isFeaturedItem = cardItem.card_mode === 'featured_product';
            const itemLink = hasItemVideo
              ? ''
              : isFeaturedItem
                ? cardItem.productSlug
                  ? `/product/${cardItem.productSlug}`
                  : '/collection'
                : cardItem.link || (item as CarouselSlide).ctaLink || '';
            const originalIndex = getOriginalIndex(index);
            // The looping carousel triples the slides purely for a seamless visual loop.
            // Every copy except the middle set is decorative — keep the duplicates out of
            // the tab order and the accessibility tree so keyboard / screen-reader users
            // reach each slide exactly once. (Tabbing into the off-screen duplicate copies
            // was what triggered the loop re-scroll and made the focus ring flash away.)
            const isDuplicate = needsLoop && (index < totalItems || index >= totalItems * 2);
            const card = carousel.type === 'card_carousel' ? (item as CarouselCard) : null;

            const belowText =
              showBelowText && card && (card.belowTitle || card.belowSubtitle) ? (
                <div className="mt-2 px-1 text-start">
                  {card.belowTitle && (
                    <p
                      className="truncate text-sm"
                      style={{
                        fontWeight: theme.card.titleWeight,
                        color: theme.card.titleColor,
                        fontFamily: theme.card.titleFont || `${theme.fonts.body}, sans-serif`,
                      }}
                    >
                      {card.belowTitle}
                    </p>
                  )}
                  {card.belowSubtitle && (
                    <p
                      className="mt-0.5 truncate text-sm"
                      style={{
                        fontWeight: theme.card.subtitleWeight || '400',
                        color: theme.card.subtitleColor,
                        fontFamily: theme.card.subtitleFont || `${theme.fonts.body}, sans-serif`,
                      }}
                    >
                      {card.belowSubtitle}
                    </p>
                  )}
                </div>
              ) : null;

            const sameCardImage =
              item.desktopImage && item.mobileImage && item.desktopImage === item.mobileImage;
            const rawCardImg = item.desktopImage || item.mobileImage || '';
            const cardResponsive = sameCardImage && isResponsiveUrl(rawCardImg);
            const mobileImgSrc = cardResponsive
              ? resolveImageUrl(rawCardImg, 'sm')
              : resolveImageUrl(item.mobileImage || item.desktopImage || '', 'sm');
            const desktopImgSrc = cardResponsive
              ? resolveImageUrl(rawCardImg, 'lg')
              : resolveImageUrl(item.desktopImage || item.mobileImage || '', 'lg');
            const isEager = index < 2;

            const imageContent = (
              <div className={`${cssId}-img overflow-hidden`} style={{ borderRadius: cardRadius }}>
                <picture>
                  {!cardResponsive && mobileImgSrc && mobileImgSrc !== desktopImgSrc && (
                    <source media="(max-width: 499px)" srcSet={mobileImgSrc} />
                  )}
                  <img
                    src={desktopImgSrc}
                    {...(cardResponsive && rawCardImg
                      ? {
                          srcSet: buildSrcSet(rawCardImg),
                          sizes: '(max-width: 499px) 100vw, 400px',
                        }
                      : {})}
                    alt={item.desktopAlt || item.mobileAlt || ''}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading={isEager ? 'eager' : 'lazy'}
                  />
                </picture>
                {hasItemVideo && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-white">
                      <svg className="ml-0.5 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            );

            return (
              <div
                key={`${item.id || 'item'}-${index}`}
                className={`${cssId} flex-shrink-0 ${snapAlign === 'center' ? 'snap-center' : 'snap-start'}`}
                data-hero-slide
                role="group"
                aria-roledescription="slide"
                // Label is just the position — the role description already announces "slide",
                // so "Slide N of M" here would make screen readers say "slide" twice.
                aria-label={`${originalIndex + 1} of ${totalItems}`}
                // Decorative duplicate copies: hide from assistive tech and remove from the
                // tab order (inert also makes every focusable descendant non-focusable).
                aria-hidden={isDuplicate || undefined}
                {...(isDuplicate ? ({ inert: '' } as Record<string, string>) : {})}
              >
                {hasItemVideo ? (
                  <button
                    type="button"
                    className="group relative block w-full cursor-pointer text-left focus:outline-none"
                    aria-label={`Play video: Slide ${originalIndex + 1}`}
                    onClick={() => setActiveVideoUrl(itemVideoUrl)}
                  >
                    {imageContent}
                    {belowText}
                    {/* Focus ring drawn as an overlay (box-shadow) above the image so it
                        repaints reliably in the scrolled/transformed track and isn't clipped. */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 z-10 opacity-0 ring-4 ring-inset ring-[#ffd100] group-focus:opacity-100"
                    />
                  </button>
                ) : itemLink ? (
                  <Link
                    to={itemLink}
                    className="group relative block w-full focus:outline-none"
                    onClick={signalScrollToProducts}
                  >
                    {imageContent}
                    {belowText}
                    {/* Focus ring drawn as an overlay (box-shadow) above the image so it
                        repaints reliably in the scrolled/transformed track and isn't clipped. */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 z-10 opacity-0 ring-4 ring-inset ring-[#ffd100] group-focus:opacity-100"
                    />
                  </Link>
                ) : (
                  <div className="w-full">
                    {imageContent}
                    {belowText}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Inside nav — desktop */}
        {theme.navigation.disabledOpacity !== 0 &&
          theme.navigation.position === 'inside' &&
          totalItems > 1 && (
            <>
              <div className="absolute top-1/2 left-4 z-10 hidden -translate-y-1/2 lg:block">
                {makeNavBtn('left')}
              </div>
              <div className="absolute top-1/2 right-4 z-10 hidden -translate-y-1/2 lg:block">
                {makeNavBtn('right')}
              </div>
            </>
          )}
        {/* Inside nav — mobile */}
        {theme.navigation.showOnMobile &&
          theme.navigation.position === 'inside' &&
          totalItems > 1 && (
            <>
              <div className="absolute top-1/2 left-2 z-10 -translate-y-1/2 lg:hidden">
                {makeNavBtn('left')}
              </div>
              <div className="absolute top-1/2 right-2 z-10 -translate-y-1/2 lg:hidden">
                {makeNavBtn('right')}
              </div>
            </>
          )}

        <p className="sr-only">
          Use left and right arrow keys to navigate. Press space to pause or resume.
        </p>
      </div>

      <ThemedIndicators
        theme={theme}
        count={totalItems}
        current={visualDotIndex}
        onSelect={goToSlide}
      />

      {/* Below nav — desktop */}
      {theme.navigation.disabledOpacity !== 0 &&
        theme.navigation.position === 'below' &&
        totalItems > 1 && (
          <div className="hidden justify-center gap-4 pt-2 lg:flex">
            {makeNavBtn('left')}
            {makeNavBtn('right')}
          </div>
        )}

      {/* Below-carousel See More (responsive per breakpoint — only renders when position is "below") */}
      {(() => {
        if (!theme.seeMore.show) return null;
        const smMeta = parseSeeMoreMeta(theme.seeMore.letterSpacing);
        const showBelow =
          smMeta.mobilePosition === 'below' ||
          smMeta.tabletPosition === 'below' ||
          smMeta.desktopPosition === 'below';
        if (!showBelow) return null;
        const belowCls = `hero-sm-below-${carousel.id || 'x'}`;
        const baseStyle: React.CSSProperties = {
          padding: `${smMeta.py}px ${smMeta.px}px`,
          borderRadius: smMeta.radius,
          display: 'block',
          textAlign: 'center' as const,
          ...(smMeta.hasBg ? { backgroundColor: smMeta.bgColor } : {}),
          ...(smMeta.smBorder ? { border: smMeta.smBorder } : {}),
          fontFamily: `${theme.fonts.button}, sans-serif`,
          fontSize: smMeta.mobileSize || theme.seeMore.size,
          fontWeight: theme.seeMore.weight,
          color: theme.seeMore.color,
          width: '100%',
        };
        return (
          <>
            <style>{`
              .${belowCls}{display:${smMeta.mobilePosition === 'below' ? 'block' : 'none'}}
              .${belowCls} a{font-size:${smMeta.mobileSize || theme.seeMore.size};width:${smMeta.mobileWidth || '85'}%;max-width:${smMeta.maxWidth || '400'}px}
              @media(min-width:768px){.${belowCls}{display:${smMeta.tabletPosition === 'below' ? 'block' : 'none'}}.${belowCls} a{font-size:${smMeta.tabletSize || theme.seeMore.size};width:100%;max-width:${smMeta.maxWidth || '400'}px}}
              @media(min-width:1024px){.${belowCls}{display:${smMeta.desktopPosition === 'below' ? 'block' : 'none'}}.${belowCls} a{font-size:${theme.seeMore.size};width:100%;max-width:${smMeta.maxWidth || '400'}px}}
            `}</style>
            <div
              className={`mt-2 px-4 ${belowCls}`}
              style={{ textAlign: smMeta.mobileAlign as any }}
            >
              <HoverLink
                to={theme.seeMore.link || '/collection'}
                style={baseStyle}
                hoverStyle={buildSeeMoreHoverStyle(smMeta, baseStyle)}
              >
                <span aria-hidden="true">{theme.seeMore.text}</span>
                <span className="sr-only">See more products</span>
              </HoverLink>
            </div>
          </>
        );
      })()}

      {activeVideoUrl && (
        <VideoModal videoUrl={activeVideoUrl} onClose={() => setActiveVideoUrl(null)} />
      )}
    </section>
  );
}

// =============================================================================
// CARD CAROUSEL
// =============================================================================

function CardCarousel({
  carousel,
  theme,
  className = '',
}: {
  carousel: Carousel;
  theme: CarouselTheme;
  className?: string;
}) {
  const isSearchOpenRef = useIsSearchOpen();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const cards = useMemo(() => carousel.content.cards || [], [carousel.content.cards]);
  const meta = carousel.content.meta;
  const config = carousel.layout_config as any;
  const originalLength = cards.length;

  const sizing = config?.sizing;
  const mobileWidthPercent = sizing?.mobile?.widthPercent ?? 100;
  const mobileMaxWidth = sizing?.mobile?.maxWidthPx ?? sizing?.fixed?.widthPx ?? 400;
  const mobileHeight = sizing?.mobile?.heightPx ?? sizing?.fixed?.heightPx ?? 230;
  const desktopMaxWidth = sizing?.desktop?.maxWidthPx ?? sizing?.fixed?.widthPx ?? 400;
  const desktopHeight = sizing?.desktop?.heightPx ?? sizing?.fixed?.heightPx ?? 230;

  const cssId = `promo-card-${carousel.id.replace(/[^a-z0-9]/g, '')}`;

  const shouldLoop = carousel.auto_advance && originalLength > 1;
  const displayCards = useMemo(
    () => (shouldLoop ? [...cards, ...cards] : cards),
    [cards, originalLength, shouldLoop],
  );

  const [slideWidth, setSlideWidth] = useState(400);
  // Theme gap for the CSS style attribute
  const themeGapStr =
    typeof theme.card.gap === 'object' ? theme.card.gap.desktop : theme.card.gap || '1rem';
  const cssGap = parseFloat(themeGapStr) * (themeGapStr.endsWith('rem') ? 16 : 1) || 16;

  useEffect(() => {
    const measure = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const style = getComputedStyle(wrapper);
      const contentWidth =
        wrapper.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      wrapper.style.setProperty('--cw', `${contentWidth}px`);
      requestAnimationFrame(() => {
        const cards = wrapper.querySelectorAll(`.${cssId}`);
        if (cards.length >= 2) {
          const first = cards[0] as HTMLElement;
          const second = cards[1] as HTMLElement;
          setSlideWidth(second.offsetLeft - first.offsetLeft);
        } else if (cards.length === 1) {
          setSlideWidth((cards[0] as HTMLElement).offsetWidth + cssGap);
        }
      });
    };
    measure();
    const timeout = setTimeout(measure, 100);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', measure);
    };
  }, [cssId, cssGap]);

  const effectiveSlideWidth = slideWidth;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(carousel.auto_advance);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const intervalMs = (carousel.auto_advance_interval || 5) * 1000;

  useEffect(() => {
    if (originalLength <= 1 || !isPlaying || isHovered || isFocused) return;
    if (typeof window !== 'undefined') {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        setIsPlaying(false);
        return;
      }
    }
    const interval = setInterval(() => {
      if (isSearchOpenRef.current) return;
      setCurrentIndex((prev) => prev + 1);
    }, intervalMs);
    return () => clearInterval(interval);
  }, [originalLength, isPlaying, isHovered, isFocused, intervalMs]);

  const handleTransitionEnd = () => {
    if (!shouldLoop) return; // No wrapping when auto-advance is off
    if (currentIndex >= originalLength) {
      setTransitionEnabled(false);
      setCurrentIndex(0);
    } else if (currentIndex < 0) {
      setTransitionEnabled(false);
      setCurrentIndex(originalLength - 1);
    }
  };

  useEffect(() => {
    if (!transitionEnabled) {
      const timeout = setTimeout(() => setTransitionEnabled(true), 50);
      return () => clearTimeout(timeout);
    }
  }, [transitionEnabled]);

  // Calculate max index so the last card aligns to the right edge of the viewport
  const wrapperWidth = wrapperRef.current?.clientWidth || 1200;
  const visibleCards = Math.floor(wrapperWidth / effectiveSlideWidth);
  const maxIndex = Math.max(0, originalLength - visibleCards);

  const handlePrev = useCallback(
    () => setCurrentIndex((prev) => (shouldLoop ? prev - 1 : Math.max(0, prev - 1))),
    [shouldLoop],
  );
  const handleNext = useCallback(
    () => setCurrentIndex((prev) => (shouldLoop ? prev + 1 : Math.min(maxIndex, prev + 1))),
    [shouldLoop, maxIndex],
  );
  const visualIndex = ((currentIndex % originalLength) + originalLength) % originalLength;
  const isAtStart = !shouldLoop && currentIndex === 0;
  const isAtEnd = !shouldLoop && currentIndex >= maxIndex;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        handlePrev();
        break;
      case 'ArrowRight':
        e.preventDefault();
        handleNext();
        break;
      case ' ':
        e.preventDefault();
        setIsPlaying(!isPlaying);
        break;
    }
  };

  // Touch swipe support — fires on touchend for natural mobile feel
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
      const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
      touchStartRef.current = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) handleNext();
        else handlePrev();
      }
    },
    [handleNext, handlePrev],
  );

  // Trackpad / horizontal wheel support — fires immediately per card
  const handlePrevRef = useRef(handlePrev);
  const handleNextRef = useRef(handleNext);
  handlePrevRef.current = handlePrev;
  handleNextRef.current = handleNext;
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let accum = 0;
    let resetId: NodeJS.Timeout;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      accum += e.deltaX;
      clearTimeout(resetId);
      resetId = setTimeout(() => {
        accum = 0;
      }, 200);
      if (accum > 50) {
        handleNextRef.current();
        accum = 0;
      } else if (accum < -50) {
        handlePrevRef.current();
        accum = 0;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  if (cards.length === 0) return null;

  const cardGap = typeof theme.card.gap === 'string' ? theme.card.gap : undefined;
  const mobileGap =
    typeof theme.card.gap === 'object' ? theme.card.gap.mobile : cardGap || '0.75rem';
  const desktopGap =
    typeof theme.card.gap === 'object' ? theme.card.gap.desktop : cardGap || '1rem';
  const cardRadius = theme.card.borderRadius;
  const hoverScale = theme.card.hoverScale;

  const nav = useThemedNav(theme);
  const showNavDesktop = theme.navigation.disabledOpacity !== 0;

  return (
    <section
      data-carousel-focus
      className={`relative w-full py-4 ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsFocused(false);
      }}
      style={{
        backgroundColor: theme.colors.sectionBackground ?? undefined,
        fontFamily: `${theme.fonts.body}, sans-serif`,
      }}
    >
      <style>{`
        .${cssId} {
          width: calc(var(--cw, 100vw) * ${mobileWidthPercent / 100});
          max-width: ${mobileMaxWidth}px;
          flex-shrink: 0;
        }
        .${cssId}-img { height: ${mobileHeight}px; }
        .${cssId} .theme-card-title { font-size: ${theme.card.titleSize.mobile || '0.875rem'}; }
        .${cssId} .theme-card-subtitle { font-size: ${theme.card.subtitleSize.mobile || '0.75rem'}; }
        @media (min-width: 640px) {
          .${cssId} .theme-card-title { font-size: ${(theme.card.titleSize.tablet ?? theme.card.titleSize.desktop) || '1rem'}; }
          .${cssId} .theme-card-subtitle { font-size: ${(theme.card.subtitleSize.tablet ?? theme.card.subtitleSize.desktop) || '0.875rem'}; }
        }
        @media (min-width: 768px) {
          .${cssId} { max-width: ${desktopMaxWidth}px; }
          .${cssId}-img { height: ${desktopHeight}px; }
        }
        @media (min-width: 1024px) {
          .${cssId} .theme-card-title { font-size: ${theme.card.titleSize.desktop || '1rem'}; }
          .${cssId} .theme-card-subtitle { font-size: ${theme.card.subtitleSize.desktop || '0.875rem'}; }
        }
      `}</style>

      <ThemedTitleRow
        theme={theme}
        meta={meta}
        carousel={carousel}
        navLeft={
          <ThemedNavButton
            style={{ ...nav.btnBase, ...(isAtStart ? { opacity: nav.disabledOpacity } : {}) }}
            hoverStyle={isAtStart ? undefined : nav.hoverStyle}
            disabled={isAtStart}
            direction="left"
            iconSize={nav.iconSize}
            isArrow={nav.isArrow}
            ariaLabel="Previous"
            onClick={handlePrev}
          />
        }
        navRight={
          <ThemedNavButton
            style={{ ...nav.btnBase, ...(isAtEnd ? { opacity: nav.disabledOpacity } : {}) }}
            hoverStyle={isAtEnd ? undefined : nav.hoverStyle}
            disabled={isAtEnd}
            direction="right"
            iconSize={nav.iconSize}
            isArrow={nav.isArrow}
            ariaLabel="Next"
            onClick={handleNext}
          />
        }
        slideCount={cards.length}
      />

      {/* Mobile: native scroll container — smooth swipe with snap */}
      <div
        className="relative md:hidden"
        role="region"
        aria-roledescription="carousel"
        aria-label={meta?.title || carousel.name}
      >
        <div
          className="flex snap-x snap-mandatory items-start overflow-x-auto py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ gap: mobileGap, paddingLeft: mobileGap, scrollPaddingLeft: mobileGap }}
        >
          {cards.map((card, index) => (
            <ThemedCardItem
              key={`${card.id || 'card'}-mobile-${index}`}
              card={card}
              carouselId={carousel.id}
              carouselName={carousel.name}
              index={index}
              totalCards={originalLength}
              cardClass={cssId}
              theme={theme}
              cardRadius={cardRadius}
              hoverScale={hoverScale}
            />
          ))}
        </div>

        {/* Mobile nav arrows — controlled by backend showOnMobile setting */}
        {theme.navigation.showOnMobile && cards.length > 1 && (
          <>
            {theme.navigation.position === 'inside' && (
              <>
                <div className="absolute top-1/2 left-2 z-10 -translate-y-1/2">
                  <ThemedNavButton
                    style={nav.btnBase}
                    hoverStyle={nav.hoverStyle}
                    direction="left"
                    iconSize={nav.iconSize}
                    isArrow={nav.isArrow}
                    ariaLabel="Previous"
                    onClick={handlePrev}
                  />
                </div>
                <div className="absolute top-1/2 right-2 z-10 -translate-y-1/2">
                  <ThemedNavButton
                    style={nav.btnBase}
                    hoverStyle={nav.hoverStyle}
                    direction="right"
                    iconSize={nav.iconSize}
                    isArrow={nav.isArrow}
                    ariaLabel="Next"
                    onClick={handleNext}
                  />
                </div>
              </>
            )}
            {theme.navigation.position === 'below' && (
              <div className="flex justify-center gap-4 pt-2">
                <ThemedNavButton
                  style={nav.btnBase}
                  hoverStyle={nav.hoverStyle}
                  direction="left"
                  iconSize={nav.iconSize}
                  isArrow={nav.isArrow}
                  ariaLabel="Previous"
                  onClick={handlePrev}
                />
                <ThemedNavButton
                  style={nav.btnBase}
                  hoverStyle={nav.hoverStyle}
                  direction="right"
                  iconSize={nav.iconSize}
                  isArrow={nav.isArrow}
                  ariaLabel="Next"
                  onClick={handleNext}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Desktop: strip with translateX — buttons and auto-advance */}
      <div
        ref={wrapperRef}
        className="relative mx-auto hidden w-full max-w-[108rem] overflow-x-clip overflow-y-visible py-3 pl-4 md:block"
        role="region"
        aria-roledescription="carousel"
        aria-label={meta?.title || carousel.name}
        tabIndex={cards.length > 1 ? 0 : -1}
        onKeyDown={handleKeyDown}
      >
        <div
          ref={stripRef}
          className={`flex ${transitionEnabled ? 'transition-transform duration-300 ease-in-out' : ''}`}
          style={{
            gap: `${cssGap}px`,
            transform: `translateX(-${currentIndex * effectiveSlideWidth}px)`,
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {displayCards.map((card, index) => (
            <ThemedCardItem
              key={`${card.id || 'card'}-${index}`}
              card={card}
              carouselId={carousel.id}
              carouselName={carousel.name}
              index={index % originalLength}
              totalCards={originalLength}
              cardClass={cssId}
              theme={theme}
              cardRadius={cardRadius}
              hoverScale={hoverScale}
            />
          ))}
        </div>

        {/* Nav arrows inside carousel area — only when position is 'inside' */}
        {showNavDesktop && theme.navigation.position === 'inside' && cards.length > 1 && (
          <>
            <div className="absolute top-1/2 left-2 z-10 hidden -translate-y-1/2 md:block">
              <ThemedNavButton
                style={{ ...nav.btnBase, ...(isAtStart ? { opacity: nav.disabledOpacity } : {}) }}
                hoverStyle={isAtStart ? undefined : nav.hoverStyle}
                disabled={isAtStart}
                direction="left"
                iconSize={nav.iconSize}
                isArrow={nav.isArrow}
                ariaLabel="Previous"
                onClick={handlePrev}
              />
            </div>
            <div className="absolute top-1/2 right-2 z-10 hidden -translate-y-1/2 md:block">
              <ThemedNavButton
                style={{ ...nav.btnBase, ...(isAtEnd ? { opacity: nav.disabledOpacity } : {}) }}
                hoverStyle={isAtEnd ? undefined : nav.hoverStyle}
                disabled={isAtEnd}
                direction="right"
                iconSize={nav.iconSize}
                isArrow={nav.isArrow}
                ariaLabel="Next"
                onClick={handleNext}
              />
            </div>
          </>
        )}

        {/* Nav arrows below carousel */}
        {showNavDesktop && theme.navigation.position === 'below' && cards.length > 1 && (
          <div className="hidden justify-center gap-4 pt-2 md:flex">
            <ThemedNavButton
              style={{ ...nav.btnBase, ...(isAtStart ? { opacity: nav.disabledOpacity } : {}) }}
              hoverStyle={isAtStart ? undefined : nav.hoverStyle}
              disabled={isAtStart}
              direction="left"
              iconSize={nav.iconSize}
              isArrow={nav.isArrow}
              ariaLabel="Previous"
              onClick={handlePrev}
            />
            <ThemedNavButton
              style={{ ...nav.btnBase, ...(isAtEnd ? { opacity: nav.disabledOpacity } : {}) }}
              hoverStyle={isAtEnd ? undefined : nav.hoverStyle}
              disabled={isAtEnd}
              direction="right"
              iconSize={nav.iconSize}
              isArrow={nav.isArrow}
              ariaLabel="Next"
              onClick={handleNext}
            />
          </div>
        )}
      </div>

      {/* Below-cards See More (responsive per breakpoint) */}
      {(() => {
        const smMeta = parseSeeMoreMeta(theme.seeMore.letterSpacing);
        const showBelow =
          smMeta.mobilePosition === 'below' ||
          smMeta.tabletPosition === 'below' ||
          smMeta.desktopPosition === 'below';
        if (theme.seeMore.show && showBelow) {
          const belowCls = `sm-below-${cssId}`;
          const baseStyle: React.CSSProperties = {
            padding: `${smMeta.py}px ${smMeta.px}px`,
            borderRadius: smMeta.radius,
            display: 'block',
            textAlign: 'center' as const,
            ...(smMeta.hasBg ? { backgroundColor: smMeta.bgColor } : {}),
            ...(smMeta.smBorder ? { border: smMeta.smBorder } : {}),
            fontFamily: `${theme.fonts.button}, sans-serif`,
            fontSize: smMeta.mobileSize || theme.seeMore.size,
            fontWeight: theme.seeMore.weight,
            color: theme.seeMore.color,
            width: '100%',
          };
          return (
            <>
              <style>{`
                .${belowCls}{display:${smMeta.mobilePosition === 'below' ? 'block' : 'none'}}
                .${belowCls} a{font-size:${smMeta.mobileSize || theme.seeMore.size};width:${smMeta.mobileWidth || '85'}%;max-width:${smMeta.maxWidth || '400'}px}
                @media(min-width:768px){.${belowCls}{display:${smMeta.tabletPosition === 'below' ? 'block' : 'none'}}.${belowCls} a{font-size:${smMeta.tabletSize || theme.seeMore.size};width:100%;max-width:${smMeta.maxWidth || '400'}px}}
                @media(min-width:1024px){.${belowCls}{display:${smMeta.desktopPosition === 'below' ? 'block' : 'none'}}.${belowCls} a{font-size:${theme.seeMore.size};width:100%;max-width:${smMeta.maxWidth || '400'}px}}
              `}</style>
              <div
                className={`mt-2 px-4 ${belowCls}`}
                style={{ textAlign: smMeta.mobileAlign as any }}
              >
                <HoverLink
                  to={theme.seeMore.link || '/collection'}
                  style={baseStyle}
                  hoverStyle={buildSeeMoreHoverStyle(smMeta, baseStyle)}
                >
                  <span aria-hidden="true">{theme.seeMore.text}</span>
                  <span className="sr-only">See more products</span>
                </HoverLink>
              </div>
            </>
          );
        }
        return null;
      })()}

      <p className="sr-only">
        Use arrow keys to navigate. Press space to pause or resume auto-advance.
      </p>
    </section>
  );
}

// =============================================================================
// THEMED CARD ITEM
// =============================================================================

function ThemedCardItem({
  card,
  carouselId,
  carouselName,
  index,
  totalCards,
  cardClass,
  theme,
  cardRadius,
  hoverScale,
}: {
  card: CarouselCard;
  carouselId: string;
  carouselName: string;
  index: number;
  totalCards: number;
  cardClass: string;
  theme: CarouselTheme;
  cardRadius: string;
  hoverScale: number;
}) {
  const [showVideo, setShowVideo] = useState(false);
  const hasVideo = !!(card.videoUrl && extractYouTubeId(card.videoUrl));

  const sameHeroImage =
    card.desktopImage && card.mobileImage && card.desktopImage === card.mobileImage;
  const rawHeroImg = card.desktopImage || card.mobileImage;
  const heroResponsive = sameHeroImage && isResponsiveUrl(rawHeroImg);
  const mobileImgSrc = heroResponsive
    ? resolveImageUrl(rawHeroImg, 'sm')
    : resolveImageUrl(card.mobileImage || card.desktopImage, 'sm');
  const desktopImgSrc = heroResponsive
    ? resolveImageUrl(rawHeroImg, 'lg')
    : resolveImageUrl(card.desktopImage || card.mobileImage, 'lg');

  const isFeaturedProduct = card.card_mode === 'featured_product';
  const rawLink = isFeaturedProduct
    ? card.productSlug
      ? `/product/${card.productSlug}`
      : '/collection'
    : card.link || '/deals';
  const cardUrl = rawLink.startsWith('/') || rawLink.startsWith('http') ? rawLink : `/${rawLink}`;

  const ariaLabel = `Card ${index + 1} of ${totalCards}${card.title ? `: ${card.title}` : ''}${
    isFeaturedProduct && card.product_name ? `: ${card.product_name}` : ''
  }`;

  const showBelowText = theme.card.titleColor !== '__hidden__';

  const cardImage = (
    <div
      className={`${cardClass}-img relative w-full overflow-hidden`}
      style={{ borderRadius: cardRadius }}
    >
      <picture>
        {!heroResponsive && mobileImgSrc && mobileImgSrc !== desktopImgSrc && (
          <source media="(max-width: 767px)" srcSet={mobileImgSrc} />
        )}
        <img
          src={desktopImgSrc || mobileImgSrc || ''}
          {...(heroResponsive && rawHeroImg
            ? {
                srcSet: buildSrcSet(rawHeroImg),
                sizes: '100vw',
              }
            : {})}
          alt={card.desktopAlt || card.mobileAlt || card.title || ''}
          className="absolute inset-0 h-full w-full object-cover"
          loading={index < 2 ? 'eager' : 'lazy'}
        />
      </picture>

      {hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-white transition-transform group-hover:scale-110">
            <svg className="ml-0.5 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );

  const belowText =
    showBelowText && (card.belowTitle || card.belowSubtitle) ? (
      <div className="mt-2 px-1 text-start">
        {card.belowTitle && (
          <p
            className="theme-card-title truncate"
            style={{
              fontWeight: theme.card.titleWeight,
              color: theme.card.titleColor,
              fontFamily: theme.card.titleFont || `${theme.fonts.body}, sans-serif`,
            }}
          >
            {card.belowTitle}
          </p>
        )}
        {card.belowSubtitle && (
          <p
            className="theme-card-subtitle mt-0.5 truncate"
            style={{
              fontWeight: theme.card.subtitleWeight || '400',
              color: theme.card.subtitleColor,
              fontFamily: theme.card.subtitleFont || `${theme.fonts.body}, sans-serif`,
            }}
          >
            {card.belowSubtitle}
          </p>
        )}
      </div>
    ) : null;

  if (hasVideo) {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowVideo(true)}
          className={`${cardClass} group block cursor-pointer text-left`}
          style={{ borderRadius: cardRadius }}
          aria-label={`Play video: ${ariaLabel}`}
        >
          {cardImage}
          {belowText}
        </button>
        {showVideo && <VideoModal videoUrl={card.videoUrl!} onClose={() => setShowVideo(false)} />}
      </>
    );
  }

  return (
    <Link
      to={cardUrl}
      className={`${cardClass} block`}
      style={{ borderRadius: cardRadius, transition: 'transform 200ms' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.transform = `scale(${hoverScale})`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
      }}
      aria-label={ariaLabel}
      onClick={signalScrollToProducts}
    >
      {cardImage}
      {belowText}
    </Link>
  );
}

// =============================================================================
// VIDEO MODAL
// =============================================================================

function VideoModal({ videoUrl, onClose }: { videoUrl: string; onClose: () => void }) {
  const videoId = extractYouTubeId(videoUrl);
  const isShort = videoUrl.includes('/shorts/');
  const onCloseRef = useRef(onClose);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!videoId) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      // Trap focus inside modal — only focusable element is the close button
      if (e.key === 'Tab') {
        e.preventDefault();
        closeBtnRef.current?.focus();
      }
    };
    const prev = document.body.style.overflow;
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    // Auto-focus close button on open
    requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [videoId]);

  if (!videoId) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9999] bg-black/95" onClick={onClose} aria-hidden="true" />
      <div
        data-carousel-focus
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Video player"
      >
        <div
          className={`relative w-full ${isShort ? 'max-w-[360px]' : 'max-w-4xl'}`}
          style={{ aspectRatio: isShort ? '9 / 16' : '16 / 9', maxHeight: '85vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            aria-label="Close video"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2.5"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
            className="absolute inset-0 h-full w-full rounded-2xl"
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="Video player"
          />
        </div>
      </div>
    </>,
    document.body,
  );
}

// =============================================================================
// ERROR BOUNDARY
// =============================================================================

export function PromotionalCarouselErrorBoundary() {
  return null;
}
